import React from "react";
import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export interface WordingGuardSuggestionChipProps {
    label: string;
    onAccept: () => void;
    onDismiss: () => void;
}

/**
 * Wording-guard suggestion: label (no click), green check, "|", red close.
 */
export const WordingGuardSuggestionChip = React.memo(function WordingGuardSuggestionChip({
    label,
    onAccept,
    onDismiss,
}: WordingGuardSuggestionChipProps) {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                pl: 1,
                pr: 0.75,
                py: 0.25,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "warning.main",
                bgcolor: "transparent",
                maxWidth: "100%",
            }}
        >
            <Box
                component="span"
                sx={{
                    typography: "body2",
                    color: "text.primary",
                    cursor: "default",
                    userSelect: "none",
                    pr: 0.25,
                }}
            >
                {label}
            </Box>
            <Box
                component="button"
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onAccept();
                }}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0.25,
                    m: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "success.main",
                    borderRadius: 1,
                    "&:focus-visible": {
                        outline: (theme) => `2px solid ${theme.palette.success.main}`,
                        outlineOffset: 2,
                    },
                }}
                aria-label="Apply wording replacement"
            >
                <CheckIcon fontSize="small" sx={{ color: "success.main" }} />
            </Box>
            <Box component="span" sx={{ typography: "body2", color: "text.secondary", userSelect: "none" }}>
                |
            </Box>
            <Box
                component="button"
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onDismiss();
                }}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0.25,
                    m: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "error.main",
                    borderRadius: 1,
                    "&:focus-visible": {
                        outline: (theme) => `2px solid ${theme.palette.error.main}`,
                        outlineOffset: 2,
                    },
                }}
                aria-label="Dismiss wording suggestion"
            >
                <CloseIcon fontSize="small" sx={{ color: "error.main" }} />
            </Box>
        </Box>
    );
});
